import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Card, Button, Input, Tag, Space, Modal, Form,
  Select, InputNumber, DatePicker, message, Typography,
  Row, Col, Tooltip, Divider, Popconfirm, Upload, Avatar
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined,
  ReloadOutlined, LockOutlined, EyeOutlined,
  CheckCircleOutlined, CloseCircleOutlined, BankOutlined,
  TeamOutlined, DollarOutlined, CameraOutlined, LoadingOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../services/api';
import './CompanyManagement.css';

const { Title, Text } = Typography;
const { Option } = Select;

/* ─── helpers ─────────────────────────────────────────── */
const statusColor = { active: 'green', inactive: 'orange', expired: 'red', suspended: 'purple' };
const fmt = (v) => parseFloat(v || 0).toLocaleString('en-IN');

// Generate password: First 4 chars of company name + @ + First 4 digits of phone
const genPasswordFromCompany = (companyName, phone) => {
  if (!companyName || !phone) return '';
  const companyPart = companyName.substring(0, 4);
  const phonePart = phone.substring(0, 4);
  return `${companyPart}@${phonePart}`;
};

const genPassword = () => {
  const c = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789@#$%';
  let p = 'A' + 'a' + '1' + '@';
  for (let i = 4; i < 10; i++) p += c[Math.floor(Math.random() * c.length)];
  return p.split('').sort(() => Math.random() - 0.5).join('');
};

/* ═══════════════════════════════════════════════════════ */
const CompanyManagement = () => {
  const [companies,    setCompanies]    = useState([]);
  const [packages,     setPackages]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [saving,       setSaving]       = useState(false);
  const [search,       setSearch]       = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [pagination,   setPagination]   = useState({ current: 1, pageSize: 10, total: 0 });

  // Modals
  const [createModal,  setCreateModal]  = useState(false);
  const [editModal,    setEditModal]    = useState(false);
  const [pwModal,      setPwModal]      = useState(false);
  const [viewModal,    setViewModal]    = useState(false);
  const [selected,     setSelected]     = useState(null);   // company being edited/viewed
  const [showPw,       setShowPw]       = useState(false);

  // Logo upload state
  const [createLogoUrl,  setCreateLogoUrl]  = useState('');   // URL after upload (create)
  const [editLogoUrl,    setEditLogoUrl]    = useState('');   // URL after upload (edit)
  const [logoUploading,  setLogoUploading]  = useState(false);

  const [createForm] = Form.useForm();
  const [editForm]   = Form.useForm();
  const [pwForm]     = Form.useForm();

  /* ── fetch ── */
  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: pagination.current, limit: pagination.pageSize };
      if (search)       params.search = search;
      if (statusFilter) params.status = statusFilter;

      const r = await api.get('/companies', { params });
      if (r.data.success) {
        setCompanies(r.data.data.companies || []);
        setPagination(p => ({ ...p, total: r.data.data.pagination?.totalRecords || 0 }));
      }
    } catch (e) {
      message.error(e.response?.data?.message || 'Failed to load companies');
    } finally { setLoading(false); }
  }, [pagination.current, pagination.pageSize, search, statusFilter]);

  const fetchPackages = async () => {
    try {
      const r = await api.get('/packages');
      if (r.data.success) setPackages(r.data.data || []);
    } catch (e) { console.error('packages load failed', e); }
  };

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);
  useEffect(() => { fetchPackages(); }, []);

  /* ── logo upload ── */
  const handleLogoUpload = async (file, mode) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) { message.error('Only image files allowed!'); return false; }
    if (file.size > 2 * 1024 * 1024) { message.error('Image must be smaller than 2MB!'); return false; }

    setLogoUploading(true);
    try {
      const formData = new FormData();
      formData.append('logo', file);
      const r = await api.post('/companies/upload-logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (r.data.success) {
        const url = r.data.data.url;
        if (mode === 'create') { setCreateLogoUrl(url); createForm.setFieldsValue({ logo: url }); }
        else                   { setEditLogoUrl(url);   editForm.setFieldsValue({ logo: url }); }
        message.success('Logo uploaded!');
      }
    } catch (e) {
      message.error(e.response?.data?.message || 'Upload failed');
    } finally { setLogoUploading(false); }
    return false; // prevent antd default upload
  };

  /* ── pricing auto-calc with tiered per-user pricing ── */
  const calcPricing = (form, basePackage = null) => {
    const packageUsers = parseInt(form.getFieldValue('packageUsers')) || 1;
    const disc = parseFloat(form.getFieldValue('discount')) || 0;
    const gstRate = parseFloat(form.getFieldValue('gstRate')) || 18;
    
    let base = 0;
    let pricePerUser = 0;

    // Tiered per-user pricing
    if (packageUsers <= 2) {
      base = 5000;
      pricePerUser = 5000;
    } else if (packageUsers >= 3 && packageUsers <= 4) {
      pricePerUser = 4500;
      base = pricePerUser * packageUsers;
    } else if (packageUsers >= 5 && packageUsers <= 9) {
      pricePerUser = 4000;
      base = pricePerUser * packageUsers;
    } else if (packageUsers >= 10) {
      pricePerUser = 3500;
      base = pricePerUser * packageUsers;
    }

    const sub = base * (1 - disc / 100);
    const gst = sub * (gstRate / 100);
    const final = sub + gst;
    
    form.setFieldsValue({
      basePrice: +base.toFixed(2),
      subtotal: +sub.toFixed(2),
      gst: +gst.toFixed(2),
      finalPrice: +final.toFixed(2)
    });
  };

  // Get pricing hint based on user count
  const getPricingHint = (users) => {
    if (!users || users < 1) return '';
    
    if (users <= 2) {
      return `💰 Rate: ₹5,000 flat (for 1-2 users)`;
    } else if (users >= 3 && users <= 4) {
      return `💰 Rate: ₹4,500/user × ${users} = ₹${(4500 * users).toLocaleString('en-IN')}`;
    } else if (users >= 5 && users <= 9) {
      return `💰 Rate: ₹4,000/user × ${users} = ₹${(4000 * users).toLocaleString('en-IN')}`;
    } else if (users >= 10) {
      return `💰 Rate: ₹3,500/user × ${users} = ₹${(3500 * users).toLocaleString('en-IN')}`;
    }
    return '';
  };

  /* ── package select helper ── */
  const [selectedCreatePackage, setSelectedCreatePackage] = useState(null);
  const [selectedEditPackage, setSelectedEditPackage] = useState(null);
  const [currentUserLimit, setCurrentUserLimit] = useState(1);

  const onPackageSelect = (pkgId, form, isEdit = false) => {
    const pkg = packages.find(p => p.id === pkgId);
    if (pkg) {
      if (isEdit) {
        setSelectedEditPackage(pkg);
      } else {
        setSelectedCreatePackage(pkg);
      }
      // Update both packageUsers and basePrice from package
      form.setFieldsValue({ 
        packageUsers: pkg.userLimit, 
        basePrice: parseFloat(pkg.price) 
      });
      setCurrentUserLimit(pkg.userLimit);
      calcPricing(form, pkg);
    } else {
      // When package is cleared, reset to default
      if (isEdit) {
        setSelectedEditPackage(null);
      } else {
        setSelectedCreatePackage(null);
      }
      const currentUsers = form.getFieldValue('packageUsers') || 1;
      setCurrentUserLimit(currentUsers);
      calcPricing(form, null);
    }
  };

  /* ── CREATE ── */
  const handleCreate = async (values) => {
    setSaving(true);
    try {
      const body = {
        ...values,
        logo:       createLogoUrl || null,
        startDate:  values.startDate?.format('YYYY-MM-DD'),
        expiryDate: values.expiryDate?.format('YYYY-MM-DD'),
      };
      const r = await api.post('/companies', body);
      if (r.data.success) {
        message.success('Company created successfully!');
        setCreateModal(false);
        setCreateLogoUrl('');
        createForm.resetFields();
        fetchCompanies();
      } else {
        message.error(r.data.message);
      }
    } catch (e) {
      message.error(e.response?.data?.message || 'Failed to create company');
    } finally { setSaving(false); }
  };

  /* ── EDIT ── */
  const openEdit = (company) => {
    setSelected(company);
    setEditLogoUrl(company.logo ? `http://localhost:3001${company.logo}` : '');
    
    // Set selected package for edit mode
    if (company.packageId) {
      const pkg = packages.find(p => p.id === company.packageId);
      if (pkg) setSelectedEditPackage(pkg);
    }
    
    editForm.setFieldsValue({
      companyName:  company.companyName,
      clientName:   company.clientName,
      phone:        company.phone,
      email:        company.email,
      username:     company.username,
      packageId:    company.packageId,
      packageUsers: company.packageUsers,
      basePrice:    parseFloat(company.basePrice),
      discount:     parseFloat(company.discount),
      gstRate:      18,
      subtotal:     parseFloat(company.subtotal),
      gst:          parseFloat(company.gst),
      finalPrice:   parseFloat(company.finalPrice),
      startDate:    company.startDate  ? dayjs(company.startDate)  : null,
      expiryDate:   company.expiryDate ? dayjs(company.expiryDate) : null,
      status:       company.status,
      logo:         company.logo || '',
    });
    setEditModal(true);
  };

  const handleEdit = async (values) => {
    setSaving(true);
    try {
      const body = {
        ...values,
        logo:       editLogoUrl ? editLogoUrl.replace('http://localhost:3001', '') : (selected.logo || null),
        startDate:  values.startDate?.format('YYYY-MM-DD'),
        expiryDate: values.expiryDate?.format('YYYY-MM-DD'),
      };
      const r = await api.put(`/companies/${selected.id}`, body);
      if (r.data.success) {
        message.success('Company updated successfully!');
        setEditModal(false);
        fetchCompanies();
      } else {
        message.error(r.data.message);
      }
    } catch (e) {
      message.error(e.response?.data?.message || 'Failed to update company');
    } finally { setSaving(false); }
  };

  /* ── CHANGE PASSWORD ── */
  const openPwModal = (company) => {
    setSelected(company);
    pwForm.resetFields();
    setShowPw(false);
    setPwModal(true);
  };

  const handleChangePassword = async (values) => {
    setSaving(true);
    try {
      const r = await api.put(`/companies/${selected.id}/change-password`, {
        newPassword: values.newPassword
      });
      if (r.data.success) {
        message.success('Password changed successfully!');
        setPwModal(false);
      } else {
        message.error(r.data.message);
      }
    } catch (e) {
      message.error(e.response?.data?.message || 'Failed to change password');
    } finally { setSaving(false); }
  };

  /* ── DELETE ── */
  const handleDelete = async (id) => {
    try {
      const r = await api.delete(`/companies/${id}`);
      if (r.data.success) {
        message.success('Company deleted');
        fetchCompanies();
      } else {
        message.error(r.data.message);
      }
    } catch (e) {
      message.error(e.response?.data?.message || 'Failed to delete company');
    }
  };

  /* ── stats ── */
  const stats = {
    total:    companies.length,
    active:   companies.filter(c => c.status === 'active').length,
    inactive: companies.filter(c => c.status !== 'active').length,
    revenue:  companies.reduce((s, c) => s + parseFloat(c.finalPrice || 0), 0)
  };

  /* ── columns ── */
  const columns = [
    {
      title: '#',
      key: 'i',
      width: 40,
      align: 'center',
      render: (_, __, i) => (
        <Text type="secondary" style={{ fontSize: 12 }}>{i + 1}</Text>
      )
    },
    {
      title: 'Company',
      dataIndex: 'companyName',
      width: 180,
      render: (text, r) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {r.logo
            ? <img src={`http://localhost:3001${r.logo}`} alt="logo"
                style={{ width: 32, height: 32, borderRadius: 6, objectFit: 'cover', border: '1px solid #f0f0f0', flexShrink: 0 }} />
            : <Avatar shape="square" size={32} style={{ background: '#1890ff', fontSize: 13, flexShrink: 0 }}>
                {text?.charAt(0).toUpperCase()}
              </Avatar>
          }
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{text}</div>
            <div style={{ fontSize: 11, color: '#888' }}>{r.clientName}</div>
          </div>
        </div>
      )
    },
    {
      title: 'Email',
      dataIndex: 'email',
      width: 200,
      render: t => (
        <Tooltip title={t}>
          <Text style={{ fontSize: 12, display: 'block', maxWidth: 180, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t}</Text>
        </Tooltip>
      )
    },
    {
      title: 'Username',
      dataIndex: 'username',
      width: 180,
      render: t => (
        <Tooltip title={t}>
          <code style={{ fontSize: 12, display: 'block', maxWidth: 160, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t}</code>
        </Tooltip>
      )
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      width: 120,
      render: t => <Text style={{ fontSize: 12 }}>{t}</Text>
    },
    {
      title: 'Package',
      dataIndex: 'Package',
      width: 210,
      render: (pkg, r) => {
        if (pkg) {
          const additionalUsers = pkg.userLimit > 1 ? ` + ${pkg.userLimit - 1} User${pkg.userLimit - 1 > 1 ? 's' : ''}` : '';
          return <Tag color="blue" style={{ whiteSpace: 'normal', lineHeight: '18px' }}>{pkg.name} (Admin{additionalUsers})</Tag>;
        }
        return <Tag>{r.packageUsers} users</Tag>;
      }
    },
    {
      title: 'Price',
      dataIndex: 'finalPrice',
      width: 100,
      align: 'right',
      render: v => <Text strong style={{ color: '#1890ff', fontSize: 13 }}>₹{fmt(v)}</Text>
    },
    {
      title: 'Expiry',
      dataIndex: 'expiryDate',
      width: 110,
      render: (d) => {
        if (!d) return <Text type="secondary">N/A</Text>;
        const days = Math.ceil((new Date(d) - new Date()) / 86400000);
        return (
          <div>
            <div style={{ fontSize: 12 }}>{new Date(d).toLocaleDateString('en-IN')}</div>
            <div style={{ fontSize: 11, fontWeight: 600, color: days < 30 ? '#f5222d' : '#52c41a' }}>
              {days > 0 ? `${days}d left` : 'Expired'}
            </div>
          </div>
        );
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 110,
      align: 'center',
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="View Details">
            <Button
              size="small"
              icon={<EyeOutlined />}
              style={{ borderColor: '#d9d9d9', color: '#595959' }}
              onClick={() => { setSelected(record); setViewModal(true); }}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              size="small"
              type="primary"
              ghost
              icon={<EditOutlined />}
              onClick={() => openEdit(record)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Popconfirm
              title={`Delete "${record.companyName}"?`}
              description="This action cannot be undone."
              okText="Delete"
              cancelText="Cancel"
              okType="danger"
              onConfirm={() => handleDelete(record.id)}
            >
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Tooltip>
        </Space>
      )
    }
  ];

  /* ── shared form fields (reused in create & edit) ── */
  const LogoUploader = ({ mode }) => {
    const logoUrl   = mode === 'create' ? createLogoUrl : editLogoUrl;
    const setLogoFn = mode === 'create' ? setCreateLogoUrl : setEditLogoUrl;

    return (
      <div style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 8, fontWeight: 500, color: '#555' }}>Company Logo</div>
        <Upload
          name="logo"
          listType="picture-card"
          showUploadList={false}
          beforeUpload={(file) => handleLogoUpload(file, mode)}
          accept="image/*"
        >
          {logoUrl ? (
            <div style={{ position: 'relative', width: 100, height: 100 }}>
              <img
                src={logoUrl}
                alt="logo"
                style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 8, border: '2px solid #1890ff' }}
              />
              <div style={{
                position: 'absolute', bottom: 0, right: 0,
                background: 'rgba(0,0,0,0.55)', borderRadius: '0 0 8px 0',
                padding: '2px 6px', cursor: 'pointer'
              }}>
                <CameraOutlined style={{ color: '#fff', fontSize: 14 }} />
              </div>
            </div>
          ) : (
            <div style={{ padding: 10 }}>
              {logoUploading ? <LoadingOutlined style={{ fontSize: 24 }} /> : <CameraOutlined style={{ fontSize: 28, color: '#1890ff' }} />}
              <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>
                {logoUploading ? 'Uploading...' : 'Click to upload'}
              </div>
              <div style={{ fontSize: 11, color: '#bbb' }}>PNG, JPG · max 2MB</div>
            </div>
          )}
        </Upload>
        {logoUrl && (
          <Button
            size="small" danger type="link"
            onClick={() => { setLogoFn(''); mode === 'create' ? createForm.setFieldsValue({ logo: '' }) : editForm.setFieldsValue({ logo: '' }); }}
            style={{ marginTop: 4 }}
          >
            Remove Logo
          </Button>
        )}
        <Form.Item name="logo" hidden><Input /></Form.Item>
      </div>
    );
  };

  const CompanyFormFields = ({ form, isEdit = false }) => (
    <>
      {/* ═══════ ROW 1: Logo + Company Name + Client Name + Email (4 fields) ═══════ */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={4}>
          <LogoUploader mode={isEdit ? 'edit' : 'create'} />
        </Col>
        <Col span={6}>
          <Form.Item name="companyName" label="Company Name" rules={[{ required: true }]}>
            <Input 
              placeholder="ABC Pvt Ltd" 
              onChange={(e) => {
                if (!isEdit) {
                  const phone = form.getFieldValue('phone');
                  const companyValue = e.target.value;
                  if (companyValue && phone && phone.length >= 4) {
                    const pwd = genPasswordFromCompany(companyValue, phone);
                    form.setFieldsValue({ password: pwd });
                  }
                }
              }}
            />
          </Form.Item>
        </Col>
        <Col span={7}>
          <Form.Item name="clientName" label="Client Name" rules={[{ required: true }]}>
            <Input placeholder="Rahul Sharma" />
          </Form.Item>
        </Col>
        <Col span={7}>
          <Form.Item name="email" label="Email Address" rules={[{ required: true, type: 'email' }]}>
            <Input placeholder="admin@company.com" prefix={<span style={{ color: '#999' }}>✉️</span>} />
          </Form.Item>
        </Col>
      </Row>

      {/* ═══════ ROW 2: Phone + Username + Password + Package (4 fields) ═══════ */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Form.Item name="phone" label="Phone Number" rules={[{ required: true, pattern: /^[0-9]{10}$/, message: '10 digit number required' }]}>
            <Input 
              placeholder="9876543210" 
              maxLength={10}
              prefix={<span style={{ color: '#999' }}>📱</span>}
              onChange={(e) => {
                if (!isEdit) {
                  const companyName = form.getFieldValue('companyName');
                  const phoneValue = e.target.value;
                  if (companyName && phoneValue && phoneValue.length >= 4) {
                    const pwd = genPasswordFromCompany(companyName, phoneValue);
                    form.setFieldsValue({ password: pwd });
                  }
                }
              }}
            />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name="username" label="Username" rules={[{ required: true }]}>
            <Input placeholder="admin@fosstech.com" prefix={<span style={{ color: '#999' }}>👤</span>} />
          </Form.Item>
        </Col>
        {!isEdit && (
          <Col span={6}>
            <Form.Item 
              name="password" 
              label="Password" 
              rules={[{ required: true, min: 6 }]}
              tooltip="Auto-generated based on company name + phone"
            >
              <Input.Password
                placeholder="••••••••••"
                prefix={<span style={{ color: '#999' }}>🔒</span>}
                iconRender={visible => (visible ? '👁️' : '🙈')}
              />
            </Form.Item>
          </Col>
        )}
        <Col span={!isEdit ? 6 : 12}>
          <Form.Item name="packageId" label="Package (Optional)">
            <Select 
              placeholder="Select package" 
              allowClear 
              onChange={(v) => onPackageSelect(v, form, isEdit)}
            >
              {packages.map(p => {
                const additionalUsers = p.userLimit > 1 ? ` + ${p.userLimit - 1} User${p.userLimit - 1 > 1 ? 's' : ''}` : '';
                return (
                  <Option key={p.id} value={p.id}>
                    {p.name} (Admin{additionalUsers})
                  </Option>
                );
              })}
            </Select>
          </Form.Item>
        </Col>
      </Row>
{/* 
      <Divider style={{ margin: '12px 0', borderColor: '#e0e0e0' }}>
        <span style={{ color: '#1890ff', fontWeight: 600, fontSize: '13px' }}>💰 Pricing Details</span>
      </Divider> */}

      {/* ═══════ ROW 3: User Qty + Base Price + Discount + Final Price (4 equal fields) ═══════ */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Form.Item name="packageUsers" label="User Qty" rules={[{ required: true }]}>
            <InputNumber 
              min={1} 
              max={100}
              style={{ width: '100%' }} 
              placeholder="1"
              onChange={(value) => {
                setCurrentUserLimit(value || 1);
                const pkg = isEdit ? selectedEditPackage : selectedCreatePackage;
                calcPricing(form, pkg);
              }}
            />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name="basePrice" label="Base Price" rules={[{ required: true }]}>
            <InputNumber 
              min={0} 
              style={{ width: '100%' }} 
              disabled
              formatter={value => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value.replace(/₹\s?|(,*)/g, '')}
            />
          </Form.Item>
        </Col>
      
        <Col span={6}>
          <Form.Item name="finalPrice" label="Final Price (Inc. GST)" rules={[{ required: true }]}>
            <InputNumber 
              min={0} 
              style={{ width: '100%', fontWeight: 700, fontSize: '15px', color: '#1890ff' }} 
              disabled
              formatter={value => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value.replace(/₹\s?|(,*)/g, '')}
            />
          </Form.Item>
        </Col>
          <Col span={6}>
          <Form.Item name="discount" label="Discount (%)">
            <InputNumber 
              min={0} 
              max={50} 
              style={{ width: '100%' }} 
              placeholder="0"
              onChange={() => {
                const pkg = isEdit ? selectedEditPackage : selectedCreatePackage;
                calcPricing(form, pkg);
              }} 
            />
          </Form.Item>
        </Col>
      </Row>

      {/* Pricing hint below Row 3 */}
      {currentUserLimit > 0 && (
        <Row gutter={16} style={{ marginTop: -12, marginBottom: 16 }}>
          <Col span={24}>
            <div style={{ fontSize: '11px', lineHeight: '1.4', padding: '8px 12px', background: '#f0f9ff', borderRadius: '6px', border: '1px solid #bae7ff' }}>
              <div style={{ color: '#1890ff', fontWeight: 600, marginBottom: '4px' }}>
                {getPricingHint(currentUserLimit)}
              </div>
              <div style={{ color: '#666', fontSize: '10px' }}>
                📊 Pricing Tiers: 1-2 users = ₹5,000 flat | 3-4 users = ₹4,500/user | 5-9 users = ₹4,000/user | 10+ users = ₹3,500/user
              </div>
            </div>
          </Col>
        </Row>
      )}

      <Divider style={{ margin: '12px 0', borderColor: '#e0e0e0' }}>
        <span style={{ color: '#722ed1', fontWeight: 600, fontSize: '13px' }}>📅 Subscription Period</span>
      </Divider>

      {/* ═══════ ROW 4: Subscription Period (Start Date + Expiry Date + Status - showing inputs) ═══════ */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={8}>
          <Form.Item name="startDate" label="Start Date" rules={[{ required: true }]}>
            <DatePicker 
              style={{ width: '100%' }} 
              format="DD/MM/YYYY"
              placeholder="Select start date"
              onChange={(date) => {
                if (date) {
                  const expiryDate = dayjs(date).add(1, 'year');
                  form.setFieldsValue({ expiryDate });
                }
              }}
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="expiryDate" label="Expiry Date" rules={[{ required: true }]}>
            <DatePicker 
              style={{ width: '100%' }} 
              format="DD/MM/YYYY"
              placeholder="Auto-calculated (+1 year)"
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="status" label="Status" initialValue="active">
            <Select>
              <Option value="active">✅ Active</Option>
              <Option value="inactive">⏸️ Inactive</Option>
              <Option value="expired">❌ Expired</Option>
              <Option value="suspended">🚫 Suspended</Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>

      {/* Hidden fields for calculations */}
      <Form.Item name="gstRate" hidden initialValue={18}><Input /></Form.Item>
      <Form.Item name="subtotal" hidden><Input /></Form.Item>
      <Form.Item name="gst" hidden><Input /></Form.Item>
    </>
  );

  /* ═══════════════ RENDER ═══════════════ */
  return (
    <div className="company-management" style={{ padding: '16px', height: '100vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
        <Col>
          <Title level={3} style={{ margin: 0, fontSize: '20px' }}>Company Management</Title>
          <Text type="secondary" style={{ fontSize: '13px' }}>Manage all registered companies</Text>
        </Col>
        <Col>
          <Space>
            <Tooltip title="Refresh"><Button icon={<ReloadOutlined />} onClick={fetchCompanies} loading={loading} /></Tooltip>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => { 
              createForm.resetFields(); 
              setCreateLogoUrl(''); 
              setCreateModal(true); 
            }}>
              Add Company
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Stats */}
      <Row gutter={12} style={{ marginBottom: 12 }}>
        {[
          { label: 'Total Companies',  value: pagination.total || stats.total, color: '#1890ff', icon: <BankOutlined />, filter: null },
          { label: 'Active',           value: stats.active,   color: '#52c41a', icon: <CheckCircleOutlined />, filter: 'active' },
          { label: 'Inactive/Expired', value: stats.inactive, color: '#f5222d', icon: <CloseCircleOutlined />, filter: 'inactive' },
          { label: 'Total Revenue',    value: `₹${fmt(stats.revenue)}`, color: '#722ed1', icon: <DollarOutlined />, filter: null },
        ].map((s, i) => (
          <Col span={6} key={i}>
            <Card 
              className={`stat-card ${statusFilter === s.filter ? 'stat-card-active' : ''}`}
              size="small" 
              bodyStyle={{ padding: '14px 16px' }} 
              style={{ 
                borderLeft: `4px solid ${s.color}`, 
                borderRadius: 8,
                cursor: s.filter ? 'pointer' : 'default',
                transition: 'all 0.3s ease',
                boxShadow: statusFilter === s.filter ? `0 4px 12px ${s.color}33` : '0 2px 4px rgba(0,0,0,0.06)',
              }}
              onClick={() => {
                if (s.filter) {
                  // Toggle filter: if already selected, clear it; otherwise, set it
                  const newFilter = statusFilter === s.filter ? '' : s.filter;
                  setStatusFilter(newFilter);
                  setPagination(p => ({ ...p, current: 1 }));
                }
              }}
              hoverable={!!s.filter}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div 
                  style={{ 
                    fontSize: 28, 
                    color: s.color,
                    background: `${s.color}15`,
                    borderRadius: 10,
                    width: 48,
                    height: 48,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s ease'
                  }}
                  className="stat-icon"
                >
                  {s.icon}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 22, fontWeight: 700, color: s.color, lineHeight: 1.2 }}>{s.value}</div>
                  <div style={{ fontSize: 12, color: '#666', marginTop: 4, fontWeight: 500 }}>{s.label}</div>
                </div>
              </div>
              {s.filter && statusFilter === s.filter && (
                <div style={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  background: s.color,
                  color: 'white',
                  borderRadius: '50%',
                  width: 20,
                  height: 20,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 12,
                  fontWeight: 700
                }}>
                  ✓
                </div>
              )}
            </Card>
          </Col>
        ))}
      </Row>

      {/* Filters */}
      <Card style={{ borderRadius: 8, marginBottom: 12 }} bodyStyle={{ padding: '10px 12px' }}>
        <Row gutter={12} align="middle">
          <Col span={10}>
            <Input
              placeholder="Search company, client, email, phone..."
              prefix={<SearchOutlined />}
              value={search}
              onChange={e => { setSearch(e.target.value); setPagination(p => ({ ...p, current: 1 })); }}
              allowClear
              size="small"
            />
          </Col>
          <Col span={6}>
            <Select placeholder="Filter by Status" style={{ width: '100%' }} allowClear
              value={statusFilter || undefined}
              onChange={v => { setStatusFilter(v || ''); setPagination(p => ({ ...p, current: 1 })); }}
              size="small">
              <Option value="active">Active</Option>
              <Option value="inactive">Inactive</Option>
              <Option value="expired">Expired</Option>
              <Option value="suspended">Suspended</Option>
            </Select>
          </Col>
          <Col>
            <Button onClick={() => { setSearch(''); setStatusFilter(''); }} size="small">Clear</Button>
          </Col>
        </Row>
      </Card>

      {/* Table */}
      <Card style={{ borderRadius: 8, flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }} bodyStyle={{ flex: 1, overflow: 'auto', padding: '12px' }}>
        <Table
          dataSource={companies}
          columns={columns}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1250, y: 'calc(100vh - 390px)' }}
          size="small"
          pagination={{
            ...pagination,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
            showTotal: (t, r) => `${r[0]}–${r[1]} of ${t}`,
            onChange: (page, pageSize) => setPagination(p => ({ ...p, current: page, pageSize })),
            size: 'small'
          }}
          locale={{ emptyText: 'No companies found. Click "Add Company" to create one.' }}
        />
      </Card>

      {/* ── CREATE MODAL ── */}
      <Modal 
        title="➕ Add New Company" 
        open={createModal} 
        onCancel={() => { 
          setCreateModal(false); 
          setCreateLogoUrl(''); 
          setSelectedCreatePackage(null);
        }}
        footer={null} 
        width={1200}
        centered
        destroyOnClose
        bodyStyle={{ padding: '20px', maxHeight: '80vh', overflowY: 'auto' }}
      >
        <Form form={createForm} layout="vertical" onFinish={handleCreate}
          initialValues={{ discount: 0, gstRate: 18, status: 'active', packageUsers: 1 }}>
          <CompanyFormFields form={createForm} isEdit={false} />
          <Row justify="end" style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
            <Space>
              <Button onClick={() => { setCreateModal(false); setSelectedCreatePackage(null); }}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={saving} icon={<PlusOutlined />} size="large">
                Create Company
              </Button>
            </Space>
          </Row>
        </Form>
      </Modal>

      {/* ── EDIT MODAL ── */}
      <Modal 
        title={`✏️ Edit: ${selected?.companyName || ''}`} 
        open={editModal}
        onCancel={() => { 
          setEditModal(false); 
          setEditLogoUrl(''); 
          setSelectedEditPackage(null);
        }} 
        footer={null} 
        width={1200}
        centered
        destroyOnClose
        bodyStyle={{ padding: '20px', maxHeight: '80vh', overflowY: 'auto' }}
      >
        <Form form={editForm} layout="vertical" onFinish={handleEdit}>
          <CompanyFormFields form={editForm} isEdit={true} />
          <Row justify="end" style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
            <Space>
              <Button onClick={() => { setEditModal(false); setSelectedEditPackage(null); }}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={saving} icon={<EditOutlined />} size="large">
                Update Company
              </Button>
            </Space>
          </Row>
        </Form>
      </Modal>

      {/* ── CHANGE PASSWORD MODAL ── */}
      <Modal title={`🔑 Change Password — ${selected?.companyName || ''}`}
        open={pwModal} onCancel={() => setPwModal(false)} footer={null} width={420} destroyOnClose>
        <Form form={pwForm} layout="vertical" onFinish={handleChangePassword}>
          <Form.Item name="newPassword" label="New Password"
            rules={[{ required: true, min: 6, message: 'Min 6 characters' }]}>
            <Input.Password
              placeholder="Enter new password"
              visibilityToggle={{ visible: showPw, onVisibleChange: setShowPw }}
              addonAfter={
                <Button size="small" type="link" style={{ padding: 0, height: 'auto' }}
                  onClick={() => pwForm.setFieldsValue({ newPassword: genPassword() })}>
                  Generate
                </Button>
              }
            />
          </Form.Item>
          <Form.Item name="confirmPassword" label="Confirm Password"
            dependencies={['newPassword']}
            rules={[{ required: true },
              ({ getFieldValue }) => ({
                validator(_, v) {
                  return !v || getFieldValue('newPassword') === v
                    ? Promise.resolve()
                    : Promise.reject('Passwords do not match');
                }
              })
            ]}>
            <Input.Password placeholder="Re-enter password" />
          </Form.Item>
          <div style={{ background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 6, padding: 10, marginBottom: 16 }}>
            <Text style={{ fontSize: 12 }}>
              ⚠️ Password change will also update the linked admin user login.
            </Text>
          </div>
          <Row justify="end">
            <Space>
              <Button onClick={() => setPwModal(false)}>Cancel</Button>
              <Button type="primary" danger htmlType="submit" loading={saving} icon={<LockOutlined />}>
                Change Password
              </Button>
            </Space>
          </Row>
        </Form>
      </Modal>

      {/* ── VIEW MODAL ── */}
      <Modal title={`🏢 ${selected?.companyName || 'Company Details'}`}
        open={viewModal} onCancel={() => setViewModal(false)}
        footer={[
          <Button key="login" type="primary" style={{ background: '#52c41a', borderColor: '#52c41a' }}
            icon={<EyeOutlined />}
            onClick={() => {
              const loginId = selected?.email || selected?.username || '';
              const pwd = selected?.plainPassword || '';
              const url = `http://localhost:3003/login?email=${encodeURIComponent(loginId)}&password=${encodeURIComponent(pwd)}`;
              window.open(url, '_blank');
            }}>
            🚀 Open Company Dashboard
          </Button>,
          <Button key="edit" type="primary" icon={<EditOutlined />}
            onClick={() => { setViewModal(false); openEdit(selected); }}>
            Edit
          </Button>,
          <Button key="close" onClick={() => setViewModal(false)}>Close</Button>
        ]}
        width={600}>
        {selected && (
          <div style={{ lineHeight: 2 }}>
            {/* Logo preview in view modal */}
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              {selected.logo
                ? <img src={`http://localhost:3001${selected.logo}`} alt="logo"
                    style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 10, border: '2px solid #1890ff' }} />
                : <Avatar shape="square" size={80} style={{ background: '#1890ff', fontSize: 28 }}>
                    {selected.companyName?.charAt(0).toUpperCase()}
                  </Avatar>
              }
            </div>

            {/* ── LOGIN CREDENTIALS BOX ── */}
            <div style={{
              background: 'linear-gradient(135deg, #e6f7ff 0%, #f0f5ff 100%)',
              border: '2px solid #1890ff',
              borderRadius: 10,
              padding: '16px 20px',
              marginBottom: 16
            }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#1890ff', marginBottom: 10 }}>
                🔐 Login Credentials
              </div>
              <Row gutter={16}>
                <Col span={12}>
                  <div style={{ fontSize: 12, color: '#888' }}>Username / Email</div>
                  <div style={{
                    fontFamily: 'monospace', fontSize: 14, fontWeight: 600,
                    background: '#fff', padding: '6px 10px', borderRadius: 6,
                    border: '1px solid #d9d9d9', marginTop: 4,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between'
                  }}>
                    <span>{selected.username || selected.email}</span>
                    <Button size="small" type="link" style={{ padding: 0, height: 'auto', fontSize: 11 }}
                      onClick={() => { navigator.clipboard.writeText(selected.username || selected.email); message.success('Copied!'); }}>
                      Copy
                    </Button>
                  </div>
                </Col>
                <Col span={12}>
                  <div style={{ fontSize: 12, color: '#888' }}>Password (Plain Text)</div>
                  <div style={{
                    fontFamily: 'monospace', fontSize: 14, fontWeight: 600,
                    background: '#fff', padding: '6px 10px', borderRadius: 6,
                    border: '1px solid #d9d9d9', marginTop: 4,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between'
                  }}>
                    <span>{selected.plainPassword || <span style={{ color: '#aaa', fontStyle: 'italic', fontSize: 12 }}>Use "Change Password" to set & view</span>}</span>
                    {selected.plainPassword && (
                      <Button size="small" type="link" style={{ padding: 0, height: 'auto', fontSize: 11 }}
                        onClick={() => { navigator.clipboard.writeText(selected.plainPassword); message.success('Copied!'); }}>
                        Copy
                      </Button>
                    )}
                  </div>
                </Col>
              </Row>
              <div style={{ marginTop: 12 }}>
                <Button
                  type="primary"
                  block
                  size="large"
                  style={{ background: '#52c41a', borderColor: '#52c41a', fontWeight: 600 }}
                  icon={<span style={{ marginRight: 6 }}>🚀</span>}
                  onClick={() => {
                    const loginId = selected?.email || selected?.username || '';
                    const pwd = selected?.plainPassword || '';
                    const url = `http://localhost:3003/login?email=${encodeURIComponent(loginId)}&password=${encodeURIComponent(pwd)}`;
                    window.open(url, '_blank');
                  }}
                >
                  Open Company Dashboard 
                </Button>
                <div style={{ fontSize: 11, color: '#888', textAlign: 'center', marginTop: 6 }}>
                  Login with above credentials at https://app.fosstrack.com/
                </div>
              </div>
            </div>

            <Row gutter={16}>
              <Col span={12}>
                <Text type="secondary">Company Name</Text><br />
                <Text strong>{selected.companyName}</Text>
              </Col>
              <Col span={12}>
                <Text type="secondary">Client Name</Text><br />
                <Text strong>{selected.clientName}</Text>
              </Col>
              <Col span={12}>
                <Text type="secondary">Email</Text><br />
                <Text>{selected.email}</Text>
              </Col>
              <Col span={12}>
                <Text type="secondary">Phone</Text><br />
                <Text>{selected.phone}</Text>
              </Col>
              <Col span={12}>
                <Text type="secondary">Status</Text><br />
                <Tag color={statusColor[selected.status]}>{selected.status?.toUpperCase()}</Tag>
              </Col>
              <Col span={12}>
                <Text type="secondary">Package</Text><br />
                <Text>{selected.Package?.name || `${selected.packageUsers} users`}</Text>
              </Col>
              <Col span={8}>
                <Text type="secondary">Base Price</Text><br />
                <Text>₹{fmt(selected.basePrice)}</Text>
              </Col>
              <Col span={8}>
                <Text type="secondary">Discount</Text><br />
                <Text>{fmt(selected.discount)}%</Text>
              </Col>
              <Col span={8}>
                <Text type="secondary">Final Price</Text><br />
                <Text strong style={{ color: '#1890ff' }}>₹{fmt(selected.finalPrice)}</Text>
              </Col>
              <Col span={12}>
                <Text type="secondary">Start Date</Text><br />
                <Text>{selected.startDate ? new Date(selected.startDate).toLocaleDateString('en-IN') : 'N/A'}</Text>
              </Col>
              <Col span={12}>
                <Text type="secondary">Expiry Date</Text><br />
                <Text>{selected.expiryDate ? new Date(selected.expiryDate).toLocaleDateString('en-IN') : 'N/A'}</Text>
              </Col>
            </Row>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default CompanyManagement;
